import { ActionDetailsWrapper } from "@app/components/actions/ActionDetailsWrapper";
import { ChildAgentActivityTimeline } from "@app/components/actions/mcp/details/ChildAgentActivityTimeline";
import { ToolGeneratedFileDetails } from "@app/components/actions/mcp/details/MCPToolOutputDetails";
import type {
  ActionDetailsDisplayContext,
  ToolExecutionDetailsProps,
} from "@app/components/actions/mcp/details/types";
import { AttachmentCitation } from "@app/components/assistant/conversation/attachment/AttachmentCitation";
import { markdownCitationToAttachmentCitation } from "@app/components/assistant/conversation/attachment/utils";
import type { PendingToolCall } from "@app/components/assistant/conversation/types";
import {
  CitationsContext,
  CiteBlock,
  getCiteDirective,
} from "@app/components/markdown/CiteBlock";
import type { MCPReferenceCitation } from "@app/components/markdown/MCPReferenceCitation";
import {
  getTaskDirectiveBlock,
  taskDirective,
} from "@app/components/markdown/TaskDirectiveBlock";
import { getIcon } from "@app/components/resources/resources_icons";
import { useChildAgentStream } from "@app/hooks/useChildAgentStream";
import { getMcpServerViewDisplayName } from "@app/lib/actions/mcp_helper";
import type { ToolGeneratedFileType } from "@app/lib/actions/mcp_internal_actions/output_schemas";
import {
  isAgentPauseOutputResourceType,
  isRunAgentQueryProgressOutput,
  isRunAgentQueryResourceType,
  isRunAgentResultResourceType,
  isStoreResourceProgressOutput,
  isToolGeneratedFile,
} from "@app/lib/actions/mcp_internal_actions/output_schemas";
import type { MCPServerViewType } from "@app/lib/api/mcp";
import {
  agentMentionDirective,
  getAgentMentionPlugin,
} from "@app/lib/mentions/markdown/plugin";
import { useAgentConfiguration } from "@app/lib/swr/assistants";
import { useMCPServerViews } from "@app/lib/swr/mcp_servers";
import { useSpaces } from "@app/lib/swr/spaces";
import { emptyArray } from "@app/lib/swr/swr";
import type { AgentConfigurationType } from "@app/types/assistant/agent";
import type { InlineActivityStep } from "@app/types/assistant/conversation";
import type { AllSupportedWithDustSpecificFileContentType } from "@app/types/files";
import type { LightWorkspaceType } from "@app/types/user";
import {
  AttachmentChip,
  Avatar,
  Button,
  CitationGrid,
  ContentMessage,
  LinkExternal01,
  Markdown,
  Robot,
} from "@dust-tt/sparkle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Components } from "react-markdown";
import type { PluggableList } from "react-markdown/lib/react-markdown";

export function MCPRunAgentActionDetails({
  lastNotification,
  owner,
  toolOutput,
  toolParams,
  displayContext,
}: ToolExecutionDetailsProps) {
  const addedMCPServerViewIds: string[] = useMemo(() => {
    if (!toolParams["toolsetsToAdd"]) {
      return emptyArray();
    }
    return toolParams["toolsetsToAdd"] as string[];
  }, [toolParams]);

  const { spaces } = useSpaces({
    workspaceId: owner.sId,
    kinds: ["global"],
    disabled: addedMCPServerViewIds.length === 0,
  });
  const { serverViews: mcpServerViews } = useMCPServerViews({
    owner,
    space: spaces[0] ?? undefined,
    availability: "all",
    disabled: addedMCPServerViewIds.length === 0,
  });

  const queryResource =
    toolOutput?.findLast(isRunAgentQueryResourceType) ?? null;
  const resultResource = toolOutput?.find(isRunAgentResultResourceType) ?? null;
  const handoverResource =
    toolOutput?.find(isAgentPauseOutputResourceType) ?? null;

  const generatedFiles =
    toolOutput?.filter(isToolGeneratedFile).map((o) => o.resource) ?? [];

  const [query, setQuery] = useState<string | null>(
    () => queryResource?.resource.text ?? null
  );
  const [childAgentId, setChildAgentId] = useState<string | null>(
    () => queryResource?.resource.childAgentId ?? null
  );

  useEffect(() => {
    if (queryResource?.resource.text) {
      setQuery(queryResource.resource.text);
    }
    if (queryResource?.resource.childAgentId) {
      setChildAgentId(queryResource.resource.childAgentId);
    }
  }, [queryResource]);

  // Primary path: IDs are stored in the query resource once the child agent message exists.
  const childStreamIds = useMemo(() => {
    const { conversationId, agentMessageId } = queryResource?.resource ?? {};
    if (conversationId && agentMessageId) {
      return { conversationId, agentMessageId };
    }
    return null;
  }, [queryResource]);

  // Fast path: IDs from the live notification when the panel is open during streaming.
  const [streamIdsFromNotification, setStreamIdsFromNotification] = useState<{
    conversationId: string;
    agentMessageId: string;
  } | null>(null);

  useEffect(() => {
    const output = lastNotification?._meta.data.output;
    if (!output) {
      return;
    }
    // Populate query/childAgentId from the store_resource notification (fast path for live runs,
    // since toolOutput is null until agent_action_success fires).
    if (isStoreResourceProgressOutput(output)) {
      const r = output.contents.find(isRunAgentQueryResourceType);
      if (r?.resource.text) {
        setQuery(r.resource.text);
      }
      if (r?.resource.childAgentId) {
        setChildAgentId(r.resource.childAgentId);
      }
    }
    if (isRunAgentQueryProgressOutput(output) && output.agentMessageId) {
      setStreamIdsFromNotification({
        conversationId: output.conversationId,
        agentMessageId: output.agentMessageId,
      });
    }
  }, [lastNotification]);

  const resolvedStreamIds = childStreamIds ?? streamIdsFromNotification;

  // Subscribe to the child agent's event stream.
  const {
    response: streamingResponse,
    isStreamingResponse,
    inlineActivitySteps,
    pendingToolCalls,
    activeCotContent,
    isDone: isStreamDone,
    isError: isStreamError,
  } = useChildAgentStream({
    childStreamIds: resolvedStreamIds,
    owner,
    // We only stream when we are in the sidebar, in the conversation we only show the query.
    disabled: displayContext === "conversation",
  });

  const { agentConfiguration: childAgent } = useAgentConfiguration({
    workspaceId: owner.sId,
    agentConfigurationId: childAgentId,
  });

  const response = resultResource?.resource.text ?? streamingResponse;

  const conversationUrl = useMemo(() => {
    if (resultResource) {
      return resultResource.resource.uri;
    }
    if (resolvedStreamIds) {
      return `/w/${owner.sId}/conversation/${resolvedStreamIds.conversationId}`;
    }
    return null;
  }, [resultResource, resolvedStreamIds, owner.sId]);

  const references = useMemo(() => {
    if (!resultResource?.resource.refs) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(resultResource.resource.refs).map(([ref, citation]) => [
        ref,
        {
          ref,
          provider: citation.provider,
          contentType:
            citation.contentType as AllSupportedWithDustSpecificFileContentType,
          title: citation.title,
          href: citation.href,
          description: citation.description,
        },
      ])
    ) satisfies Record<string, MCPReferenceCitation>;
  }, [resultResource]);

  if (!childAgent) {
    return null;
  }

  return (
    <MCPRunAgentActionDetailsDisplay
      displayContext={displayContext}
      owner={owner}
      query={query}
      childAgent={childAgent}
      isBusy={resultResource === null}
      isStreamingResponse={resultResource === null && isStreamingResponse}
      response={response}
      conversationUrl={conversationUrl}
      references={references}
      addedMCPServerViewIds={addedMCPServerViewIds}
      mcpServerViews={mcpServerViews}
      handoverResource={handoverResource}
      generatedFiles={generatedFiles}
      inlineActivitySteps={inlineActivitySteps}
      pendingToolCalls={pendingToolCalls}
      activeCotContent={activeCotContent}
      isStreamDone={isStreamDone || resultResource !== null}
      isStreamError={isStreamError}
    />
  );
}

interface MCPRunAgentActionDetailsDisplayProps {
  displayContext: ActionDetailsDisplayContext;
  owner: LightWorkspaceType;
  query: string | null;
  childAgent: AgentConfigurationType;
  isBusy: boolean;
  isStreamingResponse: boolean;
  response: string | null;
  conversationUrl: string | null;
  references: Record<string, MCPReferenceCitation>;
  addedMCPServerViewIds: string[];
  mcpServerViews: MCPServerViewType[];
  handoverResource: { resource: { text: string } } | null;
  generatedFiles: ToolGeneratedFileType[];
  inlineActivitySteps: InlineActivityStep[];
  pendingToolCalls: PendingToolCall[];
  activeCotContent: string;
  isStreamDone: boolean;
  isStreamError: boolean;
}

function MCPRunAgentActionDetailsDisplay({
  displayContext,
  owner,
  query,
  childAgent,
  isBusy,
  isStreamingResponse,
  response,
  conversationUrl,
  references,
  addedMCPServerViewIds,
  mcpServerViews,
  handoverResource,
  generatedFiles,
  inlineActivitySteps,
  pendingToolCalls,
  activeCotContent,
  isStreamDone,
  isStreamError,
}: MCPRunAgentActionDetailsDisplayProps) {
  const { t } = useTranslation();
  const [activeReferences, setActiveReferences] = useState<
    { index: number; document: MCPReferenceCitation }[]
  >([]);

  const updateActiveReferences = useCallback(
    (doc: MCPReferenceCitation, index: number) => {
      setActiveReferences((prev) => {
        if (prev.find((r) => r.index === index)) {
          return prev;
        }
        return [...prev, { index, document: doc }];
      });
    },
    []
  );

  const citationsContextValue = useMemo(
    () => ({ references, updateActiveReferences }),
    [references, updateActiveReferences]
  );

  const additionalMarkdownPlugins: PluggableList = useMemo(
    () => [getCiteDirective(), agentMentionDirective, taskDirective],
    []
  );

  const additionalMarkdownComponents: Components = useMemo(
    () => ({
      sup: CiteBlock,
      // Warning: we can't rename easily `mention` to agent_mention, because the messages DB contains this name
      mention: getAgentMentionPlugin(owner),
      project_task: getTaskDirectiveBlock(owner),
    }),
    [owner]
  );

  const agentName = childAgent.name;

  return (
    <ActionDetailsWrapper
      displayContext={displayContext}
      actionName={
        displayContext === "conversation"
          ? t("assistant.runAgentRunningTitle", "Running @{{agentName}}", {
              agentName,
            })
          : t("assistant.runAgentRunTitle", "Run @{{agentName}}", {
              agentName,
            })
      }
      visual={
        childAgent?.pictureUrl
          ? () => (
              <Avatar visual={childAgent.pictureUrl} size="xs" busy={isBusy} />
            )
          : Robot
      }
    >
      {displayContext === "conversation" ? (
        query && (
          <div className="text-sm font-normal text-muted-foreground">
            {query}
          </div>
        )
      ) : (
        <div className="flex flex-col gap-4 pl-6 pt-4">
          <div className="flex flex-col gap-4">
            {query && childAgent && (
              <div className="text-sm font-normal text-muted-foreground">
                <ContentMessage
                  title={t("assistant.runAgentQueryTitle", "Query")}
                  variant="primary"
                  size="lg"
                >
                  <Markdown
                    content={query}
                    isStreaming={false}
                    forcedTextSize="text-sm"
                    textColor="text-muted-foreground"
                    isLastMessage={false}
                    additionalMarkdownPlugins={additionalMarkdownPlugins}
                    additionalMarkdownComponents={additionalMarkdownComponents}
                  />
                </ContentMessage>
              </div>
            )}

            {addedMCPServerViewIds.length > 0 && (
              <div className="text-sm font-normal text-muted-foreground">
                <ContentMessage
                  title={t("assistant.runAgentAddedToolsTitle", "Added Tools")}
                  variant="primary"
                  size="lg"
                >
                  {addedMCPServerViewIds.map((id) => {
                    const mcpServerView = mcpServerViews.find(
                      (v) => v.sId === id
                    );
                    if (!mcpServerView) {
                      return null;
                    }
                    return (
                      <AttachmentChip
                        key={id}
                        label={getMcpServerViewDisplayName(mcpServerView)}
                        icon={{ visual: getIcon(mcpServerView.server.icon) }}
                        color="highlight"
                      />
                    );
                  })}
                </ContentMessage>
              </div>
            )}
            {handoverResource && (
              <div className="text-sm font-normal text-muted-foreground">
                <ContentMessage
                  title={t("assistant.runAgentHandoffTitle", "Handoff")}
                  variant="primary"
                  size="lg"
                >
                  <Markdown
                    content={handoverResource.resource.text}
                    additionalMarkdownPlugins={additionalMarkdownPlugins}
                    additionalMarkdownComponents={additionalMarkdownComponents}
                  />
                </ContentMessage>
              </div>
            )}
            {childAgent &&
              (inlineActivitySteps.length > 0 ||
                pendingToolCalls.length > 0 ||
                activeCotContent.length > 0 ||
                response) && (
                <>
                  <div className="flex items-center justify-between py-2">
                    <span className="font-medium text-foreground">
                      {t(
                        "assistant.runAgentAnswerTitle",
                        "@{{agentName}}'s Answer",
                        {
                          agentName: childAgent.name,
                        }
                      )}
                    </span>
                    {conversationUrl && (
                      <Button
                        icon={LinkExternal01}
                        label={t(
                          "assistant.runAgentViewFullConversation",
                          "View full conversation"
                        )}
                        variant="outline"
                        onClick={() => window.open(conversationUrl, "_blank")}
                        size="xs"
                      />
                    )}
                  </div>
                  <ChildAgentActivityTimeline
                    inlineActivitySteps={inlineActivitySteps}
                    pendingToolCalls={pendingToolCalls}
                    activeCotContent={activeCotContent}
                    isDone={isStreamDone}
                    isError={isStreamError}
                  />
                  {response && (
                    <>
                      <CitationsContext.Provider value={citationsContextValue}>
                        <Markdown
                          content={response}
                          isStreaming={isStreamingResponse}
                          isLastMessage={false}
                          additionalMarkdownPlugins={additionalMarkdownPlugins}
                          additionalMarkdownComponents={
                            additionalMarkdownComponents
                          }
                        />
                      </CitationsContext.Provider>

                      {activeReferences.length > 0 && (
                        <div className="mt-4">
                          <CitationGrid variant="grid">
                            {activeReferences
                              .sort((a, b) => a.index - b.index)
                              .map(({ document, index }) => (
                                <AttachmentCitation
                                  key={index}
                                  attachmentCitation={markdownCitationToAttachmentCitation(
                                    document
                                  )}
                                />
                              ))}
                          </CitationGrid>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            {generatedFiles.length > 0 && (
              <div className="flex flex-col gap-2">
                {generatedFiles.map((file) => (
                  <ToolGeneratedFileDetails key={file.fileId} resource={file} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </ActionDetailsWrapper>
  );
}
